import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import {
  useImpactAiPresetsQuery,
  useCreateImpactAiPresetMutation,
  useUpdateImpactAiPresetMutation,
  useDeleteImpactAiPresetMutation,
} from 'generated/graphql';

import { ImpactAiPreset, PresetFormData } from './types';

export const useImpactAiPresetManagement = () => {
  const dispatch = useDispatch();
  const [selectedPreset, setSelectedPreset] = useState<Partial<ImpactAiPreset> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [hoveredPresetId, setHoveredPresetId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPresetData, setOriginalPresetData] = useState<Partial<ImpactAiPreset> | null>(
    null
  );
  const [formData, setFormData] = useState<PresetFormData>({
    title: '',
    description: '',
    iconName: 'AutoAwesome',
    color: '#6366f1',
    enabled: true,
    relatedPresets: [],
    requiredPrivileges: [],
    sortIndex: undefined,
  });

  // GraphQL queries and mutations
  const { data, loading: isLoading } = useImpactAiPresetsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const [createPresetMutation, { loading: creating }] = useCreateImpactAiPresetMutation({
    refetchQueries: ['impactAiPresets'],
    awaitRefetchQueries: true,
  });

  const [updatePresetMutation, { loading: updating }] = useUpdateImpactAiPresetMutation({
    refetchQueries: ['impactAiPresets'],
    awaitRefetchQueries: true,
  });

  const [deletePresetMutation, { loading: deleting }] = useDeleteImpactAiPresetMutation({
    refetchQueries: ['impactAiPresets'],
    awaitRefetchQueries: true,
  });

  const presets: ImpactAiPreset[] = (data?.impactAiPresets?.items as ImpactAiPreset[]) || [];
  const isAnyOperationLoading = creating || updating || deleting;

  const handleSelectPreset = useCallback((preset: ImpactAiPreset) => {
    setSelectedPreset(preset);
    setOriginalPresetData(preset);
    setIsEditing(true);
    setIsCreating(false);
    setHasChanges(false);

    // Update form data
    setFormData({
      title: preset.title || '',
      description: preset.description || '',
      iconName: preset.iconName || 'AutoAwesome',
      color: preset.color || '#6366f1',
      enabled: (preset as any).enabled !== false, // Default to true if not specified
      relatedPresets: preset.relatedPresets || [],
      requiredPrivileges: preset.requiredPrivileges || [],
      sortIndex: preset.sortIndex,
    });
  }, []);

  // Select first preset on load if none selected
  useEffect(() => {
    if (!selectedPreset && presets.length > 0 && !isCreating) {
      handleSelectPreset(presets[0]);
    }
  }, [presets, selectedPreset, isCreating, handleSelectPreset]);

  const handleCreateNew = useCallback(() => {
    const newPresetData = {
      title: 'New Preset',
      description: 'A new ImpactAI preset',
      iconName: 'AutoAwesome',
      color: '#6366f1',
      enabled: true,
      relatedPresets: [],
      requiredPrivileges: [],
      sortIndex: undefined,
    };

    // Create a temporary preset for editing (without id)
    const tempPreset: ImpactAiPreset = {
      id: 'temp_new',
      ...newPresetData,
      actions: [],
      initialContext: {},
    };

    setSelectedPreset(tempPreset);
    setOriginalPresetData(tempPreset);
    setFormData(newPresetData);
    setIsEditing(true);
    setIsCreating(true);
    setHasChanges(false);
  }, []);

  // Helper function to clean GraphQL metadata from objects
  const cleanGraphQLData = useCallback((obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(cleanGraphQLData);
    }

    if (typeof obj === 'object') {
      const cleaned: any = {};
      Object.keys(obj).forEach((key) => {
        // Skip GraphQL and Mongoose metadata fields, plus sub preset expansion metadata
        if (
          key.startsWith('__') ||
          key === '_id' ||
          key === 'id' ||
          key === 'createdAt' ||
          key === 'updatedAt' ||
          key === 'sourcePresetId' ||
          key === 'sourcePresetTitle'
        ) {
          return;
        }
        cleaned[key] = cleanGraphQLData(obj[key]);
      });
      return cleaned;
    }

    return obj;
  }, []);

  const handleSave = useCallback(async () => {
    try {
      // Use form data and merge with existing actions
      const rawActions = selectedPreset?.actions || [];
      const cleanedActions = cleanGraphQLData(rawActions);

      const rawInitialContext = selectedPreset?.initialContext || {};
      const cleanedInitialContext = cleanGraphQLData(rawInitialContext);

      const dataToSave = {
        ...formData,
        actions: cleanedActions,
        initialContext: cleanedInitialContext,
      };

      if (selectedPreset?.id === 'temp_new') {
        // Creating a new preset
        const result = await createPresetMutation({
          variables: { data: dataToSave },
        });

        if (result.data?.createImpactAiPreset) {
          const newPreset = result.data.createImpactAiPreset as ImpactAiPreset;
          setSelectedPreset(newPreset);
          setOriginalPresetData(newPreset);
          setFormData({
            title: newPreset.title,
            description: newPreset.description,
            iconName: newPreset.iconName,
            color: newPreset.color,
            enabled: (newPreset as any).enabled !== false,
            relatedPresets: newPreset.relatedPresets || [],
            requiredPrivileges: newPreset.requiredPrivileges || [],
            sortIndex: newPreset.sortIndex,
          });
          setIsCreating(false);
          setHasChanges(false);
          dispatch(showMessageAction('Preset created successfully'));
        }
      } else if (selectedPreset?.id) {
        // Updating existing preset
        const result = await updatePresetMutation({
          variables: {
            id: selectedPreset.id,
            data: dataToSave,
          },
        });

        if (result.data?.updateImpactAiPreset) {
          const updatedPreset = result.data.updateImpactAiPreset as ImpactAiPreset;
          setSelectedPreset(updatedPreset);
          setOriginalPresetData(updatedPreset);
          setFormData({
            title: updatedPreset.title,
            description: updatedPreset.description,
            iconName: updatedPreset.iconName,
            color: updatedPreset.color,
            enabled: (updatedPreset as any).enabled !== false,
            relatedPresets: updatedPreset.relatedPresets || [],
            requiredPrivileges: updatedPreset.requiredPrivileges || [],
            sortIndex: updatedPreset.sortIndex,
          });
          setHasChanges(false);
          dispatch(showMessageAction('Preset updated successfully'));
        }
      }

      setIsEditing(false);
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'graphQLErrors' in err &&
        Array.isArray(err.graphQLErrors) &&
        err.graphQLErrors.length > 0
      ) {
        dispatch(showErrorAction(`GraphQL Error: ${err.graphQLErrors[0].message}`));
      } else if (err && typeof err === 'object' && 'networkError' in err) {
        dispatch(showErrorAction('Network error. Please check your connection and try again.'));
      } else {
        dispatch(showErrorAction('Failed to save preset. Please try again.'));
      }
      throw err;
    }
  }, [
    formData,
    selectedPreset,
    createPresetMutation,
    updatePresetMutation,
    dispatch,
    cleanGraphQLData,
  ]);

  const handleUpdatePreset = useCallback(
    async (updatedPreset: Partial<ImpactAiPreset>) => {
      try {
        const cleanedData = cleanGraphQLData(updatedPreset);

        if (isCreating) {
          // Create new preset
          const { data: createResult } = await createPresetMutation({
            variables: { data: cleanedData },
          });

          if (createResult?.createImpactAiPreset) {
            dispatch(showMessageAction('Preset created successfully'));
            setSelectedPreset(createResult.createImpactAiPreset as ImpactAiPreset);
            setIsCreating(false);
          }
        } else if (selectedPreset?.id) {
          // Update existing preset
          const { data: updateResult } = await updatePresetMutation({
            variables: {
              id: selectedPreset.id,
              data: cleanedData,
            },
          });

          if (updateResult?.updateImpactAiPreset) {
            dispatch(showMessageAction('Preset updated successfully'));
            setSelectedPreset(updateResult.updateImpactAiPreset as ImpactAiPreset);
          }
        }
      } catch (error) {
        console.error('Error saving preset:', error);
        dispatch(showErrorAction('Failed to save preset'));
        throw error; // Re-throw to allow caller to handle
      }
    },
    [
      isCreating,
      selectedPreset,
      createPresetMutation,
      updatePresetMutation,
      dispatch,
      cleanGraphQLData,
    ]
  );

  const handleDuplicate = useCallback(
    async (id: string) => {
      try {
        const presetToDuplicate = presets.find((p) => p.id === id);
        if (!presetToDuplicate) {
          return;
        }

        const cleanedPresetData = cleanGraphQLData(presetToDuplicate);
        const duplicatedData = {
          ...cleanedPresetData,
          title: `${presetToDuplicate.title} (Copy)`,
        };

        const { data: duplicateResult } = await createPresetMutation({
          variables: { data: duplicatedData },
        });

        if (duplicateResult?.createImpactAiPreset) {
          dispatch(showMessageAction('Preset duplicated successfully'));
          setSelectedPreset(duplicateResult.createImpactAiPreset as ImpactAiPreset);
        }
      } catch (error) {
        console.error('Error duplicating preset:', error);
        dispatch(showErrorAction('Failed to duplicate preset'));
        throw error;
      }
    },
    [presets, createPresetMutation, dispatch, cleanGraphQLData]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        const { data: deleteResult } = await deletePresetMutation({
          variables: { id },
        });

        // Check if deletion was successful (returns boolean)
        if (deleteResult?.deleteImpactAiPreset) {
          dispatch(showMessageAction('Preset deleted successfully'));

          // If deleting the selected preset, select another one
          if (selectedPreset?.id === id) {
            const remainingPresets = presets.filter((p) => p.id !== id);
            setSelectedPreset(remainingPresets[0] || null);
          }
        } else {
          throw new Error('Delete operation returned false');
        }
      } catch (error) {
        console.error('Error deleting preset:', error);
        dispatch(showErrorAction('Failed to delete preset'));
        throw error;
      }
    },
    [selectedPreset, presets, deletePresetMutation, dispatch]
  );

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (selectedPreset && originalPresetData) {
      setFormData({
        title: originalPresetData.title || '',
        description: originalPresetData.description || '',
        iconName: originalPresetData.iconName || 'AutoAwesome',
        color: originalPresetData.color || '#6366f1',
        enabled: (originalPresetData as any).enabled !== false,
        relatedPresets: originalPresetData.relatedPresets || [],
      });
      setSelectedPreset(originalPresetData);
    }
    setIsEditing(false);
    setHasChanges(false);
  }, [selectedPreset, originalPresetData]);

  const handleFormDataChange = useCallback((field: keyof PresetFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }, []);

  // Handle preset changes (actions, initial context, etc.)
  const handlePresetChange = useCallback((updatedPreset: Partial<ImpactAiPreset>) => {
    setSelectedPreset(updatedPreset);
    setHasChanges(true);
  }, []);

  return {
    // Data
    presets,
    selectedPreset,

    // State
    isLoading,
    isCreating,
    isAnyOperationLoading,
    isEditing,
    hoveredPresetId,
    setHoveredPresetId,
    hasChanges,

    // Individual loading states for modal
    creating,
    updating,
    deleting,

    // Form state
    formData,

    // Handlers
    handleSelectPreset,
    handleCreateNew,
    handleUpdatePreset,
    handleDuplicate,
    handleDelete,
    handleSave,
    handleStartEdit,
    handleCancelEdit,
    handleFormDataChange,
    handlePresetChange,

    // Utilities
    cleanGraphQLData,
  };
};
